/**
 * Contact page — public, no auth. Intro markdown is editable by super
 * admin via /admin/legal (slug=contact); the actual form submission hits
 * /api/public/contact and is stored in the `contact_messages` collection.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";
import { API } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import PublicLayout from "./PublicLayout";
import { PhoneInput } from "@/components/PhoneInput";

export default function ContactPage() {
    const [page, setPage] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        axios.get(`${API}/public/legal/contact`).then(r => setPage(r.data)).catch(() => {});
    }, []);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/public/contact`, form);
            toast.success(data?.note || "Message sent");
            setDone(true);
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not send message. Please email support@lawos.in.");
        } finally { setSubmitting(false); }
    };

    return (
        <PublicLayout>
            <div className="grid md:grid-cols-2 gap-10" data-testid="contact-page">
                <div>
                    {page && (
                        <>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground">{page.title}</h1>
                            <div className="mt-3 text-muted-foreground leading-relaxed">{renderMarkdown(page.body_md)}</div>
                        </>
                    )}
                    <div className="mt-6 bg-card border border-border rounded-xl px-4 py-3 inline-flex items-center gap-2 shadow-lawos-sm">
                        <Mail className="w-4 h-4 text-primary" strokeWidth={1.75}/>
                        <a href="mailto:support@lawos.in" className="text-sm text-foreground hover:text-primary" data-testid="contact-email-link">support@lawos.in</a>
                    </div>
                </div>

                <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 space-y-3 shadow-lawos-sm" data-testid="contact-form">
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-2">Send us a message</h2>
                    {done && <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs" data-testid="contact-done">Thanks — we&apos;ll reply on email within 1 business day.</div>}
                    <Input label="Your name *" value={form.name} onChange={v => update("name", v)} testId="contact-name" required minLength={2}/>
                    <Input label="Email *" type="email" value={form.email} onChange={v => update("email", v)} testId="contact-email-input" required/>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Phone (optional)</div>
                        <PhoneInput value={form.phone} onChange={(v) => update("phone", v)} testid="contact-phone"/>
                    </div>
                    <Input label="Subject *" value={form.subject} onChange={v => update("subject", v)} testId="contact-subject" required minLength={2}/>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Message *</div>
                        <textarea rows={6} value={form.message} onChange={e => update("message", e.target.value)}
                            data-testid="contact-message" required minLength={10}
                            className="w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
                    </div>
                    <button type="submit" disabled={submitting} data-testid="contact-submit"
                        className="w-full bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-30 px-4 py-2.5 text-xs uppercase tracking-[0.16em] font-medium rounded-lg shadow-lawos-sm inline-flex items-center justify-center gap-2 transition-opacity">
                        <Send className="w-3.5 h-3.5"/>{submitting ? "Sending…" : "Send message"}
                    </button>
                </form>
            </div>
        </PublicLayout>
    );
}

function Input({ label, value, onChange, type = "text", testId, ...rest }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} {...rest}
                className="w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
        </div>
    );
}
